import { useCallback, useEffect, useMemo, useState } from 'react';
import { AUTHORS } from '../../../core/constants';
import { useBooksList, useDataRowCount } from '../../../core/modules/book/hooks';
import { BlockAuthor, Exclude, Watch, MuteAuthor } from '../../../composed-components/Actions';
import { BASEFONTSIZE, COLUMNSHEADING, PAGESIZE, REFETCHDELAY, RefetchOptions } from '../constants';
import { FilterForm, Settings, Sorting, SpinnerTabs } from '../../../composed-components';
import ResizeTable from '../../../composed-components/Resize';
import { EFontSize, IBook, TColumns } from '../types/types';
import { useFilter } from '../../../context/Filter.context';
import { useSort } from '../../../context/Sort.context';
import { useUserPermit } from '../../../context/User.context';
import { TableFooter } from '../../../components/Table';
import getAllColumns from '../columns/getAllColumns';
import Pagination from '../../../components/Pagination';
import { useActions } from '../../../context/Actions.context';

const Authors = () => {
  const [fontSize, setFontSize] = useState<keyof typeof EFontSize>(BASEFONTSIZE);
  const [page, setPage] = useState<number>(1);

  const { edit_titles, move_titles_hubspot } = useUserPermit();
  const { isActionLoading, setActionLoading, isRefreshed } = useActions();
  const { filter } = useFilter();
  const { sort } = useSort();

  const changePage = p => setPage(p);
  const changeFontSize = size => setFontSize(size);

  const { data = [], isLoading, isFetching, refetch } = useBooksList(
    AUTHORS, {
      page,
      ...filter[AUTHORS],
      ...sort[AUTHORS]
    });

  const {
    count,
    isFetching: isCountFetching,
    refetch: refetchRowCount
  } = useDataRowCount(AUTHORS, { ...filter[AUTHORS] });

  const refetchTable = () => {
    setTimeout(() => {
      refetch();
      refetchRowCount();
      setActionLoading(false);
    }, REFETCHDELAY);
  };

  const sortComponent = useCallback(
    (title: string) => (
      <Sorting id={AUTHORS} title={title} changePage={changePage} />
    ),
    []
  );

  const thead = useMemo(() => {
    const allColumns = getAllColumns(sortComponent);
    const head = COLUMNSHEADING[AUTHORS].map(title => allColumns[title]);

    if (edit_titles) {
      head.push({
        title: 'Actions',
        dataIndex: 'actions',
        key: 'actions',
        className: 'show td-actions',
        fixed: 'right',
        width: 90,
        render: (_, book) => (
          <div className='actions'>
            <BlockAuthor book={book} id={AUTHORS} />
            <MuteAuthor book={book} id={AUTHORS} />
            <Watch book={book} id={AUTHORS} label={'Author'}/>
            <Exclude book={book} id={AUTHORS} label={'Author'} />
          </div>
        )
      });
    }

    return head;
  }, [edit_titles, move_titles_hubspot, sortComponent]);

  const [columns, setColumns] = useState<TColumns[]>(thead);
  const dataSource: IBook[] | undefined = data?.map(book => ({
    ...book,
    key: book['Author Id']
  }));

  useEffect(() => {
    if (!isRefreshed) {
      refetch(RefetchOptions);
      refetchRowCount(RefetchOptions);
    }
  }, [page, filter[AUTHORS], sort[AUTHORS]]);

  return (
    <>
      <div className={'wrapper-controller'}>
        <FilterForm id={AUTHORS} page={page} changePage={changePage} />
        <Settings
          thead={thead}
          id={AUTHORS}
          changeFontSize={changeFontSize}
          setColumns={setColumns}
        />
      </div>
      {!isLoading ? (
        <>
          <ResizeTable
            fontSize={fontSize}
            id={AUTHORS}
            dataSource={dataSource}
            isFetching={isFetching || isActionLoading}
            columns={columns}
            refetchTable={refetchTable}
          />
          <TableFooter>
            <Pagination
              total={count}
              isCountLoading={isCountFetching}
              current={page}
              onChange={setPage}
              pageSize={PAGESIZE}
            />
          </TableFooter>
        </>
      ) : (
        <SpinnerTabs isLoading={isLoading} />
      )}
    </>
  );
};

export default Authors;